import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router-dom";

import { IFitbitSleepSession } from "../../../../@types/Fitbit";
import { SelectMeasurementPayloadType } from "../../../../@types/Measurements";
import { IPatientTreatment } from "../../../../@types/Treatments";
import { HorizontalDots } from "../../../../assets/icons/HorizontalDots";
import { CustomIconButton } from "../../../../components/CustomIconButton";
import { Toggle } from "../../../../components/Toggle";
import { spirometryMeasurementsValues } from "../../../../constants/measurements";
import { useModal } from "../../../../hooks/useModal";
import { useUserPreferences } from "../../../../hooks/useUserPreferences";
import { userService } from "../../../../services/userService";
import { NebulizerChartRow } from "../../../../utils/chartUtils";
import { FitbitSection } from "./components/FitbitSection";
import { FitbitSleepSection } from "./components/FitbitSleepSection/FitbitSleepSection";
import { FitbitSleepSelectedSession } from "./components/FitbitSleepSelectedSession/FitbitSleepSelectedSession";
import { MeasurementSection } from "./components/MeasurementSection";
import { SpirometrySection } from "./components/SpirometrySection";
import { StepsSection } from "./components/StepsSection";
import { TreatmentSection } from "./components/TreatmentSection";
import {
  Container,
  DotsContainer,
  OptionsContainer,
  PinnedToogleContainer,
} from "./MeasurementModal.styles";
import { MeasurementModalProps } from "./MeasurementModal.types";

export function MeasurementModal({
  cardTitle,
  patientId,
  patient,
  measurementType,
  minValue,
  maxValue,
  unit,
}: MeasurementModalProps) {
  const { t } = useTranslation();

  const { portalPreferences, changePortalPreferences } = useUserPreferences();

  const { closeModal } = useModal();

  const [
    selectedSpirometryMeasurementValue,
    setSelectedSpirometryMeasurementValue,
  ] = useState<SelectMeasurementPayloadType | null>(null);

  const [hideSpirometrySection, setHideSpirometrySection] = useState(false);
  const [isPinned, setIsPinned] = useState(false);
  const [areOptionsOpen, setAreOptionsOpen] = useState(false);
  const [isTreatmentOpen, setIsTreatmentOpen] = useState(false);
  const [treatment, setTreatment] = useState<
    IPatientTreatment | NebulizerChartRow
  >();

  const [selectedSleepSession, setSelectedSleepSession] = useState<
    IFitbitSleepSession | undefined
  >(undefined);

  const isFitbitSleep = measurementType === "fitbit_sleep";

  const optionsWrapperRef = useRef<any>(null);
  const optionsButtonRef = useRef<any>(null);

  const [searchParams] = useSearchParams();
  useEffect(() => {
    const spirometryTestId = searchParams.get("spirometryTestId");
    if (spirometryTestId) {
      setSelectedSpirometryMeasurementValue({
        unformattedTime: "",
        time: "",
        fvc: 0,
        spirometryTestIds: [parseInt(spirometryTestId, 10)],
      });
    }
  }, [searchParams]);

  useEffect(() => {
    const handleClickOutside = (event: any) => {
      if (
        areOptionsOpen &&
        optionsWrapperRef.current &&
        !optionsWrapperRef.current.contains(event.target) &&
        !optionsButtonRef.current.contains(event.target)
      ) {
        setAreOptionsOpen(false);
      }
    };

    document.addEventListener("click", handleClickOutside, true);
    return () => {
      document.removeEventListener("click", handleClickOutside, true);
    };
  }, [areOptionsOpen]);

  const handlePinnedGraphChange = async (isPinnedGraph: boolean) => {
    setIsPinned(isPinnedGraph);

    setTimeout(() => {
      setAreOptionsOpen(false);
    }, 200);

    await userService.updatePinnedMeasurementType(measurementType);

    const portalPrefenrecesToChange = { ...portalPreferences };
    portalPrefenrecesToChange.pinnedMeasurementType = measurementType;
    changePortalPreferences(portalPrefenrecesToChange);

    closeModal();
  };

  const handleOnTreatmentClick = (
    treatment: IPatientTreatment | NebulizerChartRow
  ) => {
    setTreatment(treatment);
    setIsTreatmentOpen(true);
  };

  const isFitbitActivity = measurementType === "fitbit_activity";
  const isSteps = measurementType === "daily_steps";
  const isSpirometry =
    spirometryMeasurementsValues.indexOf(measurementType) !== -1;

  let measurementSectionComponent: JSX.Element;
  if (isSteps) {
    measurementSectionComponent = (
      <StepsSection
        cardTitle={cardTitle}
        patientId={patientId}
        unit={unit}
        onSelectedTableView={(showTable) => {
          setHideSpirometrySection(showTable);
        }}
      />
    );
  } else if (isFitbitActivity) {
    measurementSectionComponent = (
      <FitbitSection
        cardTitle={cardTitle}
        patientId={patientId}
        onSelectedTableView={(showTable) => {
          setHideSpirometrySection(showTable);
        }}
      />
    );
  } else if (isFitbitSleep) {
    measurementSectionComponent = (
      <FitbitSleepSection
        cardTitle={cardTitle}
        patientId={patientId}
        onSelectedTableView={(showTable) => {
          setHideSpirometrySection(showTable);
        }}
        selectedSession={selectedSleepSession}
        onSessionChange={setSelectedSleepSession}
      />
    );
  } else {
    measurementSectionComponent = (
      <MeasurementSection
        cardTitle={cardTitle}
        unit={unit}
        patientId={patientId}
        patient={patient}
        measurementType={measurementType}
        minValue={minValue}
        maxValue={maxValue}
        onSelectChartValue={(selectedChartValue) => {
          setSelectedSpirometryMeasurementValue(selectedChartValue);
        }}
        onSelectedTableView={(showMeasurementsTable) => {
          setHideSpirometrySection(showMeasurementsTable);
          setTreatment(undefined);
          setIsTreatmentOpen(false);
        }}
        onTreatmentClick={handleOnTreatmentClick}
        closeTreatmentDetails={() => {
          setTreatment(undefined);
          setIsTreatmentOpen(false);
        }}
      />
    );
  }

  return (
    <Container>
      {measurementSectionComponent}

      {isSpirometry && !hideSpirometrySection && (
        <SpirometrySection
          selectedMeasurement={selectedSpirometryMeasurementValue}
          measurementType={measurementType}
          unit={unit}
          patientId={patientId}
          isPinned={isPinned}
          onPinnedChange={handlePinnedGraphChange}
        />
      )}

      {isFitbitSleep && selectedSleepSession && !hideSpirometrySection && (
        <FitbitSleepSelectedSession session={selectedSleepSession} />
      )}

      {isTreatmentOpen && treatment && (
        <TreatmentSection treatment={treatment as NebulizerChartRow} />
      )}

      {/* For spirometry the pin toggle lives in the SpirometrySection's
          OptionsPopover (shown only while a session's details are open), so
          this standalone menu is hidden to avoid a duplicate "..." menu. */}
      {!isSpirometry && (
        <OptionsContainer>
          <DotsContainer
            onClick={() => setAreOptionsOpen((prevState) => !prevState)}
            ref={optionsButtonRef}
          >
            <CustomIconButton
              style={{ position: "absolute", top: "-5px", left: "-25px" }}
            >
              <HorizontalDots />
            </CustomIconButton>
          </DotsContainer>

          {areOptionsOpen ? (
            <PinnedToogleContainer ref={optionsWrapperRef}>
              <span>{t("patientPage_measurementModal_pinGraph")}</span>
              <Toggle
                checked={isPinned}
                onCheckedChange={handlePinnedGraphChange}
              />
            </PinnedToogleContainer>
          ) : null}
        </OptionsContainer>
      )}
    </Container>
  );
}
